var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import classNames from 'classnames';
import Icon from "@icon/Icon";
import { hasValue } from "@utils";
import { hasIcon } from "@icon/utils";
import ButtonListContext from "./ButtonListContext";
/**
 * @export
 * Enhanced Button component with multiple variants and icon support
 *
 * @example Basic button
 * ```
 * <ButtonComponent title="Click me" onClick={() => console.log('clicked')} />
 * ```
 *
 * @example Button with icons
 * ```
 * <ButtonComponent
 *   title="Save"
 *   leftIcon="💾"
 *   rightIcon="→"
 *   onClick={handleSave}
 * />
 * ```
 *
 * @example Icon only button
 * ```
 * <ButtonComponent
 *   leftIcon="×"
 *   iconOnly
 *   variant="danger"
 *   onClick={handleDelete}
 * />
 * ```
 *
 * @example Async button with loading
 * ```
 * <ButtonComponent
 *   title="Submit"
 *   loadingTitle="Submitting..."
 *   onClick={async () => await submitForm()}
 * />
 * ```
 *
 * @category Buttons
 */
const ButtonComponent = (props) => {
    const { title, tooltip, ariaLabel, leftIcon, rightIcon, className, onClick, onError, loading = false, loadingTitle, active = false, disabled = false, styles, leftIconStyles, rightIconStyles, type = 'button', variant = 'primary', iconOnly = false, size = 'medium', mode = 'transparent', textMode = 'sentence' } = props;
    // Native tooltip: use `tooltip` when given, otherwise fall back to the
    // caption so every button gets a hover hint (LPVR-577).
    const resolvedTooltip = (tooltip !== null && tooltip !== void 0 ? tooltip : title) || undefined;
    const [isLoading, setIsLoading] = React.useState(false);
    // In a list of actions (phones: More sheet) the button is a row and closes the list when tapped
    const listMode = React.useContext(ButtonListContext);
    // Toolbar mode: icon only, in the toolbar's look (secondary); without an icon it stays as is
    const asIcon = !!(listMode === null || listMode === void 0 ? void 0 : listMode.icon) && (hasIcon(leftIcon) || hasIcon(rightIcon));
    const asRow = !!listMode && !listMode.icon;
    const showIconOnly = iconOnly || asIcon;
    const shownVariant = asIcon ? 'secondary' : variant;
    const isLoadingState = loading || isLoading;
    const handleClick = (e) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (disabled || isLoadingState || !onClick)
            return;
        try {
            setIsLoading(true);
            const result = onClick(e);
            // Close the list at once: an action that waits on a dialog (a confirm) must not
            // leave the list open behind it
            (_a = listMode === null || listMode === void 0 ? void 0 : listMode.onAction) === null || _a === void 0 ? void 0 : _a.call(listMode);
            yield result;
        }
        catch (error) {
            console.error('Button onClick error:', error);
            // Pass the error too — previously only the event reached the caller, so a
            // failing async onClick could never be reported with its actual reason.
            onError === null || onError === void 0 ? void 0 : onError(e, error);
        }
        finally {
            setIsLoading(false);
        }
    });
    const renderContent = () => {
        // Busy state shows a real spinner (not the Icon skeleton). Same fa-spinner
        // used by LoadingSpinner; `spin` flows through Icon to FontAwesomeIconWrapper.
        const spinner = _jsx(Icon, { icon: ['fas', 'spinner'], spin: true });
        if (showIconOnly && !asRow) {
            return _jsx("div", { className: "uxpcore_button__icon", style: leftIconStyles || rightIconStyles || {}, children: isLoadingState ? spinner : _jsx(Icon, { icon: (leftIcon || rightIcon) }) });
        }
        // A row always has a label: an icon-only button uses its tooltip
        const rawTitle = (isLoadingState && hasValue(loadingTitle)) ? loadingTitle : title || (asRow ? resolvedTooltip || ariaLabel : '') || '';
        // sentence case = upshift only the first character (acronym-safe); uppercase/lowercase are CSS.
        const displayTitle = textMode === 'sentence' && rawTitle
            ? rawTitle.charAt(0).toUpperCase() + rawTitle.slice(1)
            : rawTitle;
        // while loading the left slot becomes the spinner (works even with no icon); right icon hides
        const leftIconElement = isLoadingState
            ? spinner
            : (hasIcon(leftIcon) ? _jsx(Icon, { icon: leftIcon }) : null);
        const rightIconElement = !isLoadingState && hasIcon(rightIcon) ? _jsx(Icon, { icon: rightIcon }) : null;
        return (_jsxs(_Fragment, { children: [!!leftIconElement ? _jsx("div", { className: "uxpcore_button__icon", style: leftIconStyles, children: leftIconElement }) : null, !!displayTitle ? _jsx("div", { className: classNames("uxpcore_button__text", {
                        "uxpcore_button__text--uppercase": textMode === 'uppercase',
                        "uxpcore_button__text--lowercase": textMode === 'lowercase',
                    }), children: displayTitle }) : null, !!displayTitle && !!rightIconElement ? _jsx("div", { className: "uxpcore_button__icon", style: rightIconStyles, children: rightIconElement }) : null] }));
    };
    const buttonClasses = classNames('uxpcore_button', className, {
        'uxpcore_button--listrow': asRow,
        [`uxpcore_button--${shownVariant}`]: shownVariant,
        [`uxpcore_button--${size}`]: size !== 'medium',
        'uxpcore_button--active': active,
        'uxpcore_button--disabled': disabled,
        'uxpcore_button--loading': isLoadingState,
        'uxpcore_button--icon-only': showIconOnly && !asRow,
        'uxpcore_button--transparent': showIconOnly && (mode === 'transparent' || asIcon),
        'uxpcore_button--has-left-icon': leftIcon && !showIconOnly,
        'uxpcore_button--has-right-icon': rightIcon && !showIconOnly,
    });
    return (_jsx("button", { className: buttonClasses, style: styles, onClick: handleClick, type: type, disabled: disabled, title: resolvedTooltip, "aria-label": showIconOnly ? resolvedTooltip || ariaLabel : undefined, children: renderContent() }));
};
export default ButtonComponent;
